import { message } from 'antd';
import i18n from '@portal/i18n/config';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

import { sendError } from './crashlytics';
import { isAxiosError, isExpectedErrorKey } from './ApiErrorHandler';

const GENERIC_ERROR = 'APPLICATION.ERRORS.GENERIC';

const toastDuration = undefined;

const toastStyles = undefined;

export const success = (key: string) => {
  return message.success({
    content: i18n.t(key ? key.toUpperCase() : GENERIC_ERROR),
    duration: toastDuration,
    style: toastStyles,
  });
};

export const info = (key: string) => {
  return message.info({
    content: i18n.t(key ? key.toUpperCase() : GENERIC_ERROR),
    duration: toastDuration,
    style: toastStyles,
  });
};

export const warn = (key: string, duration?: number) => {
  const tempDuration = duration ? duration : toastDuration;

  return message.warn({
    content: i18n.t(key ? key.toUpperCase() : GENERIC_ERROR),
    duration: tempDuration,
    style: toastStyles,
  });
};

export const show = (key: string) => {
  return message.info({
    content: i18n.t(key ? key.toUpperCase() : GENERIC_ERROR),
    duration: toastDuration,
    style: toastStyles,
  });
};

type InterpolationType = {
  interpolationField: string;
  interpolationValue: string;
};

type ErrorDomain = 'api' | 'ui' | 'validation';

export type MessageErrorOptions = {
  send?: boolean;
  interpolation?: InterpolationType;
  domain?: ErrorDomain;
  originalError?: Error | AxiosError;
  alreadyReportedToSentry?: boolean;
};

const isSdkApiError = (value: unknown): boolean =>
  typeof value === 'object' &&
  value !== null &&
  'status' in value &&
  'url' in value &&
  'method' in value;

const EXPECTED_DOMAINS: ReadonlyArray<ErrorDomain> = ['validation', 'ui'];

const resolveTranslation = (
  safeKey: string
): { translationKey: string; translated: string } => {
  if (!safeKey) {
    return {
      translationKey: GENERIC_ERROR,
      translated: i18n.t(GENERIC_ERROR),
    };
  }

  const candidates = safeKey.includes('.')
    ? [safeKey, `APPLICATION.ERRORS.${safeKey.toUpperCase()}`]
    : [`APPLICATION.ERRORS.${safeKey.toUpperCase()}`];

  for (const candidate of candidates) {
    const result = i18n.t(candidate);
    const missing =
      result === candidate || result.startsWith('APPLICATION.ERRORS.');
    if (!missing) {
      return { translationKey: candidate, translated: result };
    }
  }

  return {
    translationKey: candidates[candidates.length - 1] ?? GENERIC_ERROR,
    translated: i18n.t(GENERIC_ERROR),
  };
};

export const error = (key?: string | null, options?: MessageErrorOptions) => {
  const safeKey = key ?? '';
  const domain: ErrorDomain = options?.domain ?? 'ui';
  const isRawAxiosMessage =
    safeKey.includes('AxiosError') ||
    safeKey.includes('Request failed with status code');

  const alreadyCapturedByApiHandler =
    options?.alreadyReportedToSentry === true ||
    domain === 'api' ||
    (options?.originalError !== undefined &&
      isAxiosError(options.originalError)) ||
    (options?.originalError !== undefined &&
      isSdkApiError(options.originalError));

  if (isRawAxiosMessage) {
    Sentry.addBreadcrumb({
      category: 'message.error',
      message: 'API error with raw axios key — using generic',
      level: 'warning',
      data: { key: safeKey },
    });

    if (options?.send) {
      sendError(safeKey);
    }

    return message.error({
      content: i18n.t(GENERIC_ERROR),
      duration: toastDuration,
      style: toastStyles,
    });
  }

  const { translationKey, translated } = resolveTranslation(safeKey);
  let response = translated;

  if (options?.interpolation) {
    response = response.replace(
      `{{${options.interpolation.interpolationField}}}`,
      options.interpolation.interpolationValue
    );
  }

  if (response.toUpperCase().includes('NETWORK')) {
    response = i18n.t(GENERIC_ERROR);
  }

  if (options?.send) {
    sendError(response);
  }

  if (alreadyCapturedByApiHandler) {
    const dedupSource = options?.alreadyReportedToSentry
      ? 'sdk-flag'
      : domain === 'api'
      ? 'domain'
      : options?.originalError && isSdkApiError(options.originalError)
      ? 'sdk-api-error'
      : 'axios';
    Sentry.addBreadcrumb({
      category: 'message.error',
      message: response,
      level: 'warning',
      data: {
        key: safeKey,
        translationKey,
        deduped: dedupSource,
      },
    });
  } else {
    const isExpected =
      EXPECTED_DOMAINS.includes(domain) || isExpectedErrorKey(safeKey);

    const baseTags = {
      errorType: 'ibk',
      'error.domain': domain,
      translationKey: safeKey,
    };
    const baseExtra = {
      key: safeKey,
      response,
      translationKey,
      source: 'message.error',
    };

    Sentry.addBreadcrumb({
      category: 'message.error',
      message: response,
      level: isExpected ? 'warning' : 'error',
      data: { key: safeKey, translationKey },
    });

    if (isExpected) {
      Sentry.captureEvent({
        message: `[expected] ${safeKey || 'ui-error'}: ${response}`,
        level: 'warning',
        tags: baseTags,
        extra: baseExtra,
        fingerprint: ['ibk-expected', safeKey || 'unknown'],
        transaction: `message.${safeKey || 'ui-error'}`,
      });
    } else {
      const errorToCapture = options?.originalError ?? new Error(response);
      if (!errorToCapture.name || errorToCapture.name === 'Error') {
        errorToCapture.name = safeKey || 'IbkError';
      }
      Sentry.captureException(errorToCapture, {
        tags: baseTags,
        extra: baseExtra,
        fingerprint: ['ibk-error', safeKey || 'unknown'],
      });
    }
  }

  return message.error({
    content: response,
    duration: toastDuration,
    style: toastStyles,
  });
};

const MessageService = {
  success,
  info,
  warn,
  show,
  error,
};

export default MessageService;
